import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { lumi } from "../lib/lumi"
import { canSendMessage } from "../lib/messagingACL"
import { getEffectiveUserId, logImpersonationAction, isImpersonating, getViewedUserName } from "../lib/impersonationHelper"
import SuperadminLayout from "../components/SuperadminLayout"
import toast from "react-hot-toast"
import {Inbox, Star, Mail, FileText, AlertCircle, MessageSquare, Search, Send, Paperclip, X, Eye, Download, ChevronLeft, Filter, ArrowLeft, Plus} from 'lucide-react'

type Folder = "all" | "unread" | "starred" | "applications" | "documents" | "system" | "support" | "deleted"

interface MessageThread {
  _id: string
  participants: string[]
  subject: string
  lastSnippet: string
  unreadCount: number
  starred?: boolean
  folder?: Folder
  attachmentCount?: number
  deletedBy?: string[]
  createdAt: string
  updatedAt: string
}

interface Message {
  _id: string
  threadId: string
  fromUserId: string
  toUserIds: string[]
  bodyHtml: string
  readBy: string[]
  attachments?: Array<{ name: string; url: string; size: number }>
  deletedBy?: string[]
  createdAt: string
}

export default function MessagesPage() {
  const navigate = useNavigate()
  const [activeFolder, setActiveFolder] = useState<Folder>("all")
  const [threads, setThreads] = useState<MessageThread[]>([])
  const [selectedThread, setSelectedThread] = useState<MessageThread | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [searchQuery, setSearchQuery] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [isSending, setIsSending] = useState(false)
  const [replyBody, setReplyBody] = useState("")
  const [showComposer, setShowComposer] = useState(false)
  const [showNewMessageModal, setShowNewMessageModal] = useState(false)
  const [newMessageRecipient, setNewMessageRecipient] = useState("")
  const [newMessageSubject, setNewMessageSubject] = useState("")
  const [newMessageBody, setNewMessageBody] = useState("")
  const [adminUsers, setAdminUsers] = useState<any[]>([])
  const [allUsers, setAllUsers] = useState<any[]>([])
  const [userProfiles, setUserProfiles] = useState<Record<string, any>>({})

  // Determine back link based on user role
  const getDashboardPath = () => {
    const role = lumi.auth.user?.userRole
    if (role === "ADMIN") return "/dashboard/admin"
    if (role === "SUPERADMIN") return "/dashboard/superadmin"
    return "/dashboard/nurse"
  }

  const folders = [
    { id: "all" as Folder, label: "All Messages", icon: Inbox, color: "text-gray-600" },
    { id: "unread" as Folder, label: "Unread", icon: Mail, color: "text-blue-600" },
    { id: "starred" as Folder, label: "Starred", icon: Star, color: "text-yellow-600" },
    { id: "applications" as Folder, label: "Applications", icon: FileText, color: "text-green-600" },
    { id: "documents" as Folder, label: "Documents", icon: FileText, color: "text-purple-600" },
    { id: "system" as Folder, label: "System", icon: AlertCircle, color: "text-orange-600" },
    { id: "support" as Folder, label: "Support", icon: MessageSquare, color: "text-teal-600" },
    { id: "deleted" as Folder, label: "Deleted", icon: X, color: "text-red-600" }
  ]

  useEffect(() => {
    fetchThreads()
    const user = lumi.auth.user
    if (user?.userRole === "SUPERADMIN") {
      fetchAllUsers()
    } else {
      fetchAdminUsers()
    }
  }, [activeFolder, searchQuery])

  useEffect(() => {
    if (selectedThread) {
      fetchMessages(selectedThread._id)
      markThreadAsRead(selectedThread._id)
    }
  }, [selectedThread])

  const fetchAdminUsers = async () => {
    try {
      const user = lumi.auth.user
      if (!user) return

      const effectiveUserId = getEffectiveUserId(user.userId)

      const { list } = await lumi.entities.UserProfiles.list({
        filter: {
          role: { $in: ["admin", "admin_regional", "superadmin"] },
          userId: { $ne: effectiveUserId }  // Exclude current/impersonated user
        },
        limit: 100
      })
      
      console.log("🔍 Fetched Admin Users (RAW):", list)
      
      // Filter out any users with incorrect role data
      const validAdmins = list.filter(admin => 
        ["admin", "admin_regional", "superadmin"].includes(admin.role)
      )
      
      console.log("✅ Valid Admin Users:", validAdmins)
      
      setAdminUsers(validAdmins)
      
      // Debug: Log if any users were filtered out
      if (list.length !== validAdmins.length) {
        console.warn("⚠️ Some users had incorrect role data:", 
          list.filter(u => !validAdmins.includes(u))
        )
      }
      
      // Debug: Check for missing fullName fields
      validAdmins.forEach(admin => {
        if (!admin.fullName) {
          console.warn("⚠️ Missing fullName for admin:", {
            userId: admin.userId,
            email: admin.email,
            role: admin.role
          })
        }
      })
    } catch (error) {
      console.error("Failed to fetch admin users:", error)
    }
  }

  const fetchAllUsers = async () => {
    try {
      const user = lumi.auth.user
      if (!user) {
        console.warn("⚠️ No authenticated user - cannot fetch users")
        return
      }

      const effectiveUserId = getEffectiveUserId(user.userId)
      console.log("🔍 Fetching all users for SUPERADMIN, excluding:", effectiveUserId)

      const { list, total } = await lumi.entities.UserProfiles.list({
        filter: {
          userId: { $ne: effectiveUserId }  // Exclude current/impersonated user
        },
        limit: 500,
        sort: { role: 1, fullName: 1 }
      })
      
      console.log("✅ Fetched All Users for Superadmin:", {
        count: list.length,
        total,
        roles: list.map(u => u.role),
        sampleUsers: list.slice(0, 3).map(u => ({
          userId: u.userId,
          email: u.email,
          fullName: u.fullName,
          role: u.role
        }))
      })
      
      setAllUsers(list)
      
      if (list.length === 0) {
        console.warn("⚠️ No users found in UserProfiles collection!")
      }
    } catch (error) {
      console.error("❌ Failed to fetch all users:", error)
      toast.error("Failed to load users list")
    }
  }

  const fetchThreads = async () => {
    setIsLoading(true)
    try {
      const user = lumi.auth.user
      if (!user) {
        setThreads([])
        setIsLoading(false)
        return
      }

      // Use effective userId for impersonation support
      const effectiveUserId = getEffectiveUserId(user.userId)

      const filter: any = {
        participants: { $in: [effectiveUserId] }  // CRITICAL FIX: Use $in operator with effective userId
      }

      if (activeFolder === "deleted") {
        // Show only threads deleted by this user
        filter.deletedBy = { $in: [effectiveUserId] }
      } else {
        // Exclude deleted threads in all other folders
        filter.$or = [
          { deletedBy: { $exists: false } },
          { deletedBy: { $nin: [effectiveUserId] } }
        ]

        if (activeFolder !== "all") {
          if (activeFolder === "unread") {
            filter.unreadCount = { $gt: 0 }
          } else if (activeFolder === "starred") {
            filter.starred = true
          } else {
            filter.folder = activeFolder
          }
        }
      }

      if (searchQuery) {
        filter.$or = [
          { subject: { $regex: searchQuery, $options: "i" } },
          { lastSnippet: { $regex: searchQuery, $options: "i" } }
        ]
      }

      const { list } = await lumi.entities.MessageThreads.list({
        filter,
        sort: { updatedAt: -1 },
        limit: 50
      })
      setThreads(list)
    } catch (error) {
      console.error("Failed to fetch threads:", error)
      toast.error("Failed to load messages")
    } finally {
      setIsLoading(false)
    }
  }

  const fetchMessages = async (threadId: string) => {
    try {
      const { list } = await lumi.entities.Messages.list({
        filter: { threadId },
        sort: { createdAt: 1 }
      })
      setMessages(list)
      
      // Fetch user profiles for all message senders
      const senderIds = [...new Set(list.map(msg => msg.fromUserId))]
      await fetchUserProfiles(senderIds)
    } catch (error) {
      console.error("Failed to fetch messages:", error)
      toast.error("Failed to load conversation")
    }
  }

  const fetchUserProfiles = async (userIds: string[]) => {
    try {
      const { list } = await lumi.entities.UserProfiles.list({
        filter: { userId: { $in: userIds } },
        limit: 100
      })
      
      const profileMap: Record<string, any> = {}
      list.forEach(profile => {
        profileMap[profile.userId] = profile
      })
      
      setUserProfiles(prev => ({ ...prev, ...profileMap }))
    } catch (error) {
      console.error("Failed to fetch user profiles:", error)
    }
  }

  const markThreadAsRead = async (threadId: string) => {
    try {
      const user = lumi.auth.user
      if (!user) return

      const effectiveUserId = getEffectiveUserId(user.userId)

      // Update thread unread count
      await lumi.entities.MessageThreads.update(threadId, {
        unreadCount: 0,
        updatedAt: new Date().toISOString()
      })

      // Update messages readBy array
      const threadMessages = await lumi.entities.Messages.list({
        filter: { threadId }
      })

      for (const msg of threadMessages.list) {
        if (!msg.readBy.includes(effectiveUserId)) {
          await lumi.entities.Messages.update(msg._id, {
            readBy: [...msg.readBy, effectiveUserId],
            updatedAt: new Date().toISOString()
          })
        }
      }

      // Refresh threads
      fetchThreads()
    } catch (error) {
      console.error("Failed to mark as read:", error)
    }
  }

  const toggleStar = async (thread: MessageThread) => {
    try {
      await lumi.entities.MessageThreads.update(thread._id, {
        starred: !thread.starred,
        updatedAt: new Date().toISOString()
      })
      fetchThreads()
      toast.success(thread.starred ? "Removed from starred" : "Added to starred")
    } catch (error) {
      console.error("Failed to toggle star:", error)
      toast.error("Failed to update")
    }
  }

  const sendReply = async () => {
    if (!replyBody.trim() || !selectedThread) return

    setIsSending(true)
    try {
      const user = lumi.auth.user
      if (!user) {
        toast.error("You must be logged in")
        return
      }

      // Use effective userId for impersonation
      const effectiveUserId = getEffectiveUserId(user.userId)
      const recipientIds = selectedThread.participants.filter(id => id !== effectiveUserId)

      const newMessage = await lumi.entities.Messages.create({
        threadId: selectedThread._id,
        fromUserId: effectiveUserId,  // Message appears from impersonated user
        toUserIds: recipientIds,
        bodyHtml: `<p>${replyBody}</p>`,
        readBy: [effectiveUserId],
        attachments: [],
        creator: effectiveUserId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      // Log impersonation action if in impersonation mode
      if (isImpersonating()) {
        await logImpersonationAction(
          'message_sent',
          'Messages',
          newMessage._id,
          { threadId: selectedThread._id, recipientIds, snippet: replyBody.substring(0, 100) }
        )
      }

      // Update thread
      await lumi.entities.MessageThreads.update(selectedThread._id, {
        lastSnippet: replyBody.substring(0, 100),
        unreadCount: recipientIds.length,
        updatedAt: new Date().toISOString()
      })

      setMessages([...messages, newMessage])
      setReplyBody("")
      setShowComposer(false)
      toast.success("Message sent")
      fetchThreads()
    } catch (error) {
      console.error("Failed to send message:", error)
      toast.error("Failed to send message")
    } finally {
      setIsSending(false)
    }
  }

  const sendNewMessage = async () => {
    if (!newMessageSubject.trim() || !newMessageBody.trim() || !newMessageRecipient) {
      toast.error("Please fill all fields")
      return
    }

    setIsSending(true)
    try {
      const user = lumi.auth.user
      if (!user) {
        toast.error("You must be logged in")
        return
      }

      // Use effective userId for impersonation
      const effectiveUserId = getEffectiveUserId(user.userId)

      // Superadmin can message anyone, others need ACL check
      if (user.userRole !== "SUPERADMIN") {
        if (!canSendMessage(user.userRole, newMessageRecipient)) {
          toast.error("You don't have permission to message this recipient")
          return
        }
      }

      // Create thread
      const newThread = await lumi.entities.MessageThreads.create({
        participants: [effectiveUserId, newMessageRecipient],
        subject: newMessageSubject,
        lastSnippet: newMessageBody.substring(0, 100),
        unreadCount: 1,
        starred: false,
        folder: "all",
        attachmentCount: 0,
        creator: effectiveUserId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      // Create first message
      const firstMessage = await lumi.entities.Messages.create({
        threadId: newThread._id,
        fromUserId: effectiveUserId,  // Message appears from impersonated user
        toUserIds: [newMessageRecipient],
        bodyHtml: `<p>${newMessageBody}</p>`,
        readBy: [effectiveUserId],
        attachments: [],
        creator: effectiveUserId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      // Log impersonation action if in impersonation mode
      if (isImpersonating()) {
        await logImpersonationAction(
          'message_created',
          'Messages',
          firstMessage._id,
          { threadId: newThread._id, recipient: newMessageRecipient, subject: newMessageSubject }
        )
      }

      toast.success("Message sent successfully")
      setShowNewMessageModal(false)
      setNewMessageRecipient("")
      setNewMessageSubject("")
      setNewMessageBody("")
      fetchThreads()
      setSelectedThread(newThread)
    } catch (error) {
      console.error("Failed to send new message:", error)
      toast.error("Failed to send message")
    } finally {
      setIsSending(false)
    }
  }

  const deleteThread = async (thread: MessageThread) => {
    if (!window.confirm("Move this conversation to Deleted folder?")) return

    try {
      const user = lumi.auth.user
      if (!user) return

      const effectiveUserId = getEffectiveUserId(user.userId)
      const deletedBy = thread.deletedBy || []

      await lumi.entities.MessageThreads.update(thread._id, {
        deletedBy: [...deletedBy, effectiveUserId],
        updatedAt: new Date().toISOString()
      })

      // Log impersonation action if in impersonation mode
      if (isImpersonating()) {
        await logImpersonationAction(
          'thread_deleted',
          'MessageThreads',
          thread._id,
          { subject: thread.subject }
        )
      }

      toast.success("Conversation moved to Deleted folder")
      setSelectedThread(null)
      fetchThreads()
    } catch (error) {
      console.error("Failed to delete thread:", error)
      toast.error("Failed to delete conversation")
    }
  }

  const restoreThread = async (thread: MessageThread) => {
    if (!window.confirm("Restore this conversation?")) return

    try {
      const user = lumi.auth.user
      if (!user) return

      const effectiveUserId = getEffectiveUserId(user.userId)
      const deletedBy = (thread.deletedBy || []).filter(id => id !== effectiveUserId)

      await lumi.entities.MessageThreads.update(thread._id, {
        deletedBy: deletedBy.length > 0 ? deletedBy : undefined,
        updatedAt: new Date().toISOString()
      })

      // Log impersonation action if in impersonation mode
      if (isImpersonating()) {
        await logImpersonationAction(
          'thread_restored',
          'MessageThreads',
          thread._id,
          { subject: thread.subject }
        )
      }

      toast.success("Conversation restored")
      setSelectedThread(null)
      fetchThreads()
    } catch (error) {
      console.error("Failed to restore thread:", error)
      toast.error("Failed to restore conversation")
    }
  }

  const getUnreadCount = (folderId: Folder) => {
    if (folderId === "all") {
      return threads.reduce((sum, t) => sum + (t.unreadCount || 0), 0)
    }
    if (folderId === "unread") {
      return threads.filter(t => (t.unreadCount || 0) > 0).length
    }
    if (folderId === "starred") {
      return threads.filter(t => t.starred).length
    }
    if (folderId === "deleted") {
      return threads.length
    }
    return threads.filter(t => t.folder === folderId).length
  }

  // Check if user is superadmin for layout wrapper
  const isSuperadmin = lumi.auth.user?.userRole === "SUPERADMIN"

  const messagesContent = (
    <div className={`flex bg-gray-50 flex-col ${isSuperadmin ? 'h-full' : 'h-screen'}`}>
      {/* Header with Back Button (only for non-superadmin) */}
      {!isSuperadmin && (
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(getDashboardPath())}
            className="flex items-center gap-2 text-gray-600 hover:text-teal-600 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Dashboard</span>
          </button>
          <div className="flex-1" />
          <h1 className="text-xl font-bold text-gray-900">Messages</h1>
          {isImpersonating() && (
            <div className="bg-yellow-50 text-yellow-800 px-3 py-1 rounded-md text-sm font-medium border border-yellow-200">
              Messaging as: {getViewedUserName()}
            </div>
          )}
        </header>
      )}

      <div className="flex flex-1 overflow-hidden">
      {/* Sidebar - Folders */}
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-3">Folders</h2>
          <button
            onClick={() => setShowNewMessageModal(true)}
            className="w-full bg-teal-600 text-white py-2 rounded-lg hover:bg-teal-700 transition-colors flex items-center justify-center gap-2 font-medium"
          >
            <Plus className="w-4 h-4" />
            New Message
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto p-2">
          {folders.map((folder) => {
            const Icon = folder.icon
            const count = getUnreadCount(folder.id)
            return (
              <button
                key={folder.id}
                onClick={() => {
                  setActiveFolder(folder.id)
                  setSelectedThread(null)
                }}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors mb-1 ${
                  activeFolder === folder.id
                    ? "bg-teal-50 text-teal-700 font-semibold"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon className={`w-5 h-5 ${activeFolder === folder.id ? "text-teal-600" : folder.color}`} />
                  <span>{folder.label}</span>
                </div>
                {count > 0 && (
                  <span className="bg-teal-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {count}
                  </span>
                )}
              </button>
            )
          })}
        </nav>
      </aside>

      {/* Middle - Thread List */}
      <div className="w-96 bg-white border-r border-gray-200 flex flex-col">
        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Thread List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
          ) : threads.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <Mail className="w-12 h-12 mb-2 text-gray-300" />
              <p>No messages</p>
            </div>
          ) : (
            threads.map((thread) => (
              <button
                key={thread._id}
                onClick={() => setSelectedThread(thread)}
                className={`w-full p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors text-left ${
                  selectedThread?._id === thread._id ? "bg-teal-50 border-l-4 border-l-teal-600" : ""
                }`}
              >
                <div className="flex items-start justify-between mb-1">
                  <h3 className={`font-semibold text-gray-900 ${thread.unreadCount > 0 ? "font-bold" : ""}`}>
                    {thread.subject}
                  </h3>
                  <div className="flex items-center gap-2">
                    {thread.starred && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
                    {thread.unreadCount > 0 && (
                      <span className="bg-teal-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                        {thread.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-600 line-clamp-2 mb-2">{thread.lastSnippet}</p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{new Date(thread.updatedAt).toLocaleDateString()}</span>
                  {thread.attachmentCount > 0 && (
                    <span className="flex items-center gap-1">
                      <Paperclip className="w-3 h-3" />
                      {thread.attachmentCount}
                    </span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Right - Message Reader */}
      <div className="flex-1 flex flex-col bg-white">
        {selectedThread ? (
          <>
            {/* Thread Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSelectedThread(null)}
                  className="lg:hidden text-gray-600 hover:text-gray-900"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">{selectedThread.subject}</h2>
                  <p className="text-sm text-gray-500">{selectedThread.participants.length} participants</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleStar(selectedThread)}
                  className="text-gray-600 hover:text-yellow-500 transition-colors"
                >
                  <Star className={`w-6 h-6 ${selectedThread.starred ? "fill-yellow-500 text-yellow-500" : ""}`} />
                </button>
                {activeFolder === "deleted" ? (
                  <button
                    onClick={() => restoreThread(selectedThread)}
                    className="px-3 py-1.5 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 transition-colors"
                  >
                    Restore
                  </button>
                ) : (
                  <button
                    onClick={() => deleteThread(selectedThread)}
                    className="text-gray-600 hover:text-red-600 transition-colors"
                    title="Delete conversation"
                  >
                    <X className="w-6 h-6" />
                  </button>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {messages.map((message) => {
                const user = lumi.auth.user
                const effectiveUserId = user ? getEffectiveUserId(user.userId) : null
                const isOwn = message.fromUserId === effectiveUserId
                
                // Get sender's profile from database
                const senderProfile = userProfiles[message.fromUserId]
                const senderName = senderProfile?.fullName || senderProfile?.email || "Unknown"
                const senderRole = senderProfile?.role || "user"
                const roleDisplay = senderRole === "nurse" ? "Nurse" : senderRole === "admin" ? "Admin" : senderRole === "superadmin" ? "Superadmin" : "User"
                
                // Debug logging (remove after testing)
                console.log("Message Debug:", {
                  messageId: message._id,
                  fromUserId: message.fromUserId,
                  senderProfile,
                  senderName,
                  senderRole,
                  effectiveUserId,
                  isOwn
                })
                
                return (
                  <div key={message._id} className={`flex ${isOwn ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-2xl ${isOwn ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-900"} rounded-lg p-4`}>
                      {/* Show sender name and role for non-own messages */}
                      {!isOwn && (
                        <div className="mb-2 pb-2 border-b border-gray-200">
                          <p className="text-xs font-semibold text-gray-700">
                            {senderName} <span className="text-gray-500">({roleDisplay})</span>
                          </p>
                        </div>
                      )}
                      <div
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: message.bodyHtml }}
                      />
                      {message.attachments && message.attachments.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {message.attachments.map((att, idx) => (
                            <a
                              key={idx}
                              href={att.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`flex items-center gap-2 p-2 rounded ${
                                isOwn ? "bg-teal-700" : "bg-gray-200"
                              }`}
                            >
                              <Paperclip className="w-4 h-4" />
                              <span className="text-sm flex-1">{att.name}</span>
                              <Download className="w-4 h-4" />
                            </a>
                          ))}
                        </div>
                      )}
                      <p className={`text-xs mt-2 ${isOwn ? "text-teal-100" : "text-gray-500"}`}>
                        {new Date(message.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Composer */}
            {showComposer ? (
              <div className="p-4 border-t border-gray-200 bg-gray-50">
                <textarea
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  placeholder="Type your reply..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 resize-none"
                  rows={4}
                />
                <div className="flex items-center justify-between mt-3">
                  <button className="text-gray-600 hover:text-gray-900">
                    <Paperclip className="w-5 h-5" />
                  </button>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setShowComposer(false)
                        setReplyBody("")
                      }}
                      className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={sendReply}
                      disabled={isSending || !replyBody.trim()}
                      className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      <Send className="w-4 h-4" />
                      {isSending ? "Sending..." : "Send"}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 border-t border-gray-200">
                <button
                  onClick={() => setShowComposer(true)}
                  className="w-full bg-teal-600 text-white py-3 rounded-lg hover:bg-teal-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  Reply
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <Mail className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">Select a message</p>
              <p className="text-sm">Choose a conversation from the list to view messages</p>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* New Message Modal */}
      {showNewMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">New Message</h2>
              <button
                onClick={() => {
                  setShowNewMessageModal(false)
                  setNewMessageRecipient("")
                  setNewMessageSubject("")
                  setNewMessageBody("")
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {lumi.auth.user?.userRole === "SUPERADMIN" ? "To (Any User)" : "To (Admin/Regional Admin)"}
                  {isImpersonating() && (
                    <span className="ml-2 text-xs text-yellow-600 font-normal">
                      Sending as: {getViewedUserName()}
                    </span>
                  )}
                </label>
                <select
                  value={newMessageRecipient}
                  onChange={(e) => setNewMessageRecipient(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  disabled={lumi.auth.user?.userRole === "SUPERADMIN" && allUsers.length === 0}
                >
                  <option value="">
                    {lumi.auth.user?.userRole === "SUPERADMIN" && allUsers.length === 0 
                      ? "Loading users..." 
                      : "Select recipient..."}
                  </option>
                  {(lumi.auth.user?.userRole === "SUPERADMIN" ? allUsers : adminUsers).map((user) => {
                    const roleLabel = user.role === "admin" ? "Admin" 
                      : user.role === "admin_regional" ? "Regional Admin" 
                      : user.role === "superadmin" ? "Superadmin"
                      : user.role === "nurse" ? "Nurse"
                      : user.role === "recruiter" ? "Recruiter"
                      : user.role === "affiliate" ? "Affiliate"
                      : user.role || "User"
                    
                    // Extract name from email if fullName is missing
                    const displayName = user.fullName || user.email?.split('@')[0] || user.userId
                    
                    return (
                      <option key={user.userId} value={user.userId}>
                        {displayName} • {roleLabel}
                      </option>
                    )
                  })}
                </select>
                {(lumi.auth.user?.userRole === "SUPERADMIN" ? allUsers : adminUsers).length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">No users available</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                <input
                  type="text"
                  value={newMessageSubject}
                  onChange={(e) => setNewMessageSubject(e.target.value)}
                  placeholder="Message subject..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                <textarea
                  value={newMessageBody}
                  onChange={(e) => setNewMessageBody(e.target.value)}
                  placeholder="Type your message..."
                  rows={8}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowNewMessageModal(false)
                  setNewMessageRecipient("")
                  setNewMessageSubject("")
                  setNewMessageBody("")
                }}
                className="px-6 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={sendNewMessage}
                disabled={isSending || !newMessageRecipient || !newMessageSubject.trim() || !newMessageBody.trim()}
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                {isSending ? "Sending..." : "Send Message"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )

  // Wrap with SuperadminLayout if user is superadmin
  if (isSuperadmin) {
    return (
      <SuperadminLayout activeMenuItem="messages">
        {isImpersonating() && (
          <div className="mb-4 bg-yellow-50 text-yellow-800 px-4 py-3 rounded-md text-sm font-medium border border-yellow-200">
            Messaging as: {getViewedUserName()}
          </div>
        )}
        {messagesContent}
      </SuperadminLayout>
    )
  }

  return messagesContent
}
